import { observer } from 'mobx-react-lite'
import { toContactFormInput } from 'app/lib/mutationInputs'
import { graphql } from 'app/generated/gql'
import { withZod } from '@remix-validated-form/with-zod'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'remix-validated-form'
import { z } from 'zod'
import { zfd } from 'zod-form-data'
import { ContactDrawerContainer } from './ContactDrawerContainer'
import { ContactTagsShadcn as ContactTags } from './ContactTagsShadcn'
import PmsIntegration from './PmsIntegration'
import { RevertNonPatient } from './RevertNonPatient'
import { FollowUpForm } from 'app/components/activities/FollowUpForm'
import Button from 'app/components/Button'
import { ClosedLostForm } from 'app/components/ClosedLostForm'
import { SmartForm, SubAction } from 'app/components/form/SmartForm'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { getValidatedUser } from 'app/components/table/UserAssignCell'
import type { ActivityJson } from 'app/types/entities'
import { LOST_REASONS } from 'app/types/entities'
import type { ContactJson } from 'app/types/entities'
import type { FollowUpJson } from 'app/types/entities'
import type { LocationJson } from 'app/types/entities'
import type { StaffOptionValue } from 'app/types/entities'
import { showModal, closeModal } from 'app/lib/modal'
import { gqlMutation } from 'app/lib/graphqlClient'

import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useContactStore } from 'app/stores/StoreContext'
import { isLocationIntegrated, useUser } from 'app/utils'
import { isSMCOrg } from 'lib/isSMCOrg'
import { bg } from 'lib/background'
import { trace, debug, logError } from 'lib/logging'
import { OptionType } from 'types/OptionType'

const CLOSE_LOST_MUTATION = graphql(`
  mutation CloseLostFromDrawer($id: ID!, $input: UpdateContactInput!) {
    updateContact(id: $id, input: $input) {
      id
      leadStatus
      closedLostReason
      isQualified
    }
  }
`)

const contactValidator = {
  channel: z
    .string()
    .optional()
    .transform((channel): ContactJson[`channel`] => {
      if (!channel) return undefined
      return channel
    }),
  leadStatus: z
    .string()
    .optional()
    .transform((leadStatus): ContactJson[`leadStatus`] => {
      switch (leadStatus) {
        case `Needs Action`:
        case `In Progress`:
        case `Scheduled`:
        case `Closed Won`:
        case `Closed Lost`:
          return leadStatus
        default:
          return undefined
      }
    }),
  tags: zfd.repeatable(z.array(zfd.text())).optional(),
  isQualified: z
    .union([z.boolean(), z.string()])
    .optional()
    .transform((val): boolean | undefined => {
      if (typeof val === `boolean`) return val
      if (val === `true`) return true
      if (val === `false`) return false
      return undefined
    }),
  closedLostReason: z
    .string()
    .optional()
    .transform((value?: string): (typeof LOST_REASONS)[number] | undefined => toLostReason(value)),
  leadOwner: z
    .object({ id: z.string(), name: z.string(), value: z.string() })
    .optional()
    .transform((leadOwner?: OptionType): StaffOptionValue | undefined => {
      if (!leadOwner?.id?.trim() && !leadOwner?.name?.trim()) return undefined
      return { id: leadOwner.id, name: leadOwner.name }
    }),
  isMarkedNonPatient: z.boolean().optional(),
  isSMC: z.string().optional(),
  trackableCampaignId: z.string().optional(),
  utmCampaign: z.string().optional(),
  utmAccountId: z.string().optional(),
  utmCampaignId: z.string().optional(),
  utmSource: z.string().optional(),
  utmMedium: z.string().optional(),
  reportedAttribution: z.string().optional(),
}

function toLostReason(value?: string): (typeof LOST_REASONS)[number] | undefined {
  return LOST_REASONS.find((reason) => reason === value)
}

const contactFormSchema = z
  .object({
    id: z.string().min(1, { message: `Contact Id required` }),
    patientId: z.string().optional(),
    ...contactValidator,
  })
  .superRefine((data, ctx) => {
    if (data.leadStatus === `Closed Lost` && data.closedLostReason === undefined && data.isSMC === `false`) {
      ctx.addIssue({ code: `custom`, message: `Closed Lost Reason is required`, path: [`leadStatus`] })
    }
  })

type ContactFormValues = z.infer<typeof contactFormSchema>

export const contactValidatorSchema = withZod(contactFormSchema)

type Props = {
  contact: ContactJson
  location: LocationJson
  isAgentOnly?: boolean
  isAIThread?: boolean
  onChange: (contact: ContactJson) => void
  onSaved?: () => void
  onActivityAdded?: (activity: ActivityJson) => void
  onSaving?: (isSaving: boolean) => void
}

/**
 * ContactInfo - Contact information component with 4 main sections
 *
 * Section Structure:
 * 1. Contact Details - Name, age, location + ContactActions button
 * 2. Tags - 2-line max display with overflow chip (+N) for hidden tags
 * 3. Customizable Container - Contains 3 reorderable subsections:
 *    - Spotlight: Key fields with customize button
 *    - Pipelines: Counter badge + add to pipeline
 *    - Additional Fields: Collapsible dropdown (closed by default)
 * 4. Save/Cancel Actions - Conditional display based on form changes
 *    (triggers on name changes, but excludes tag changes)
 *
 * @returns ContactInfo component with new layout structure
 */
// observer: reads a feature toggle (and possibly authStore.user) in render; useFeatureToggle resolves async and its contract requires an observer wrapper (TRK-6020).
export const ContactInfo = observer(function ContactInfo({
  contact,
  location,
  onChange,
  onSaved,
  onActivityAdded,
  onSaving,
  isAgentOnly = false,
  isAIThread,
}: Props): ReactElement {
  const isIntegrated = isLocationIntegrated(location)
  const isMarkedNonPatient = useMemo(() => contact.patientStatus === `NonPatient`, [contact.patientStatus])
  const [isDirtyForm, setIsDirtyForm] = useState<boolean>(false)
  const [formKey, setFormKey] = useState<number>(0)
  const user = useUser()
  const contactStore = useContactStore()
  const staffDashboardV2 = useFeatureToggle(`staff-dashboard-v2`)
  const formCtx = useFormContext(`contact-drawer-form`)
  const [contactInFlight, setContactInFlight] = useState(false)
  const [validatedLeadOwner, setValidatedLeadOwner] = useState<StaffOptionValue | undefined>(undefined)

  const effectiveLeadOwner = staffDashboardV2 ? validatedLeadOwner : contact.leadOwner

  // Validate contact owner on mount
  useEffect(() => {
    if (!staffDashboardV2) return
    if (!contact.leadOwner?.id) return
    bg(async () => {
      const result = await getValidatedUser(contact.leadOwner, contact.workspaceId)
      const validated = result ? ({ id: result.id, name: result.name } as StaffOptionValue) : undefined
      if (validated?.id !== validatedLeadOwner?.id || validated?.name !== validatedLeadOwner?.name) {
        setValidatedLeadOwner(validated)
        setFormKey((prev) => prev + 1)
      }
    })
  }, [contact.leadOwner?.id, staffDashboardV2])

  // Create defaultValues following ContactInfo.tsx pattern
  const defaultValues = useMemo<Partial<ContactFormValues>>(
    () => ({
      ...contact,
      closedLostReason: toLostReason(contact.closedLostReason ?? undefined),
      leadOwner: effectiveLeadOwner
        ? {
            id: effectiveLeadOwner.id || effectiveLeadOwner.name,
            name: effectiveLeadOwner.name,
          }
        : undefined,
    }),
    [contact, contact.leadStatus, effectiveLeadOwner],
  )

  // Handle form dirty state detection - called when any field changes
  const handleTouchedFields = (touched: boolean): void => {
    setIsDirtyForm(touched)
  }

  // Communicate in-flight state to parent to prevent drawer closing
  useEffect(() => {
    if (onSaving) {
      onSaving(contactInFlight)
    }
  }, [contactInFlight])

  if (!user) return <></>

  const updateContact = async (data: Record<string, unknown>) => {
    setContactInFlight(true)
    try {
      const input = toContactFormInput(data)
      trace(`contacts`, `updateContact V2`, input)
      const updateResult = await contactStore.update(contact.id!, input)
      if (updateResult.error) {
        logError(`Error updating contact V2`, updateResult.error.message)
        notifyError(`Error updating contact`)
        return
      }
      const result = contactStore.current
      if (result) {
        notifySuccess(`Save Successful`)
        onSaved?.()
        setIsDirtyForm(false)
        onChange({ ...contact, leadStatus: result.leadStatus })
      }
    } catch (error) {
      logError(`Error updating contact`, error)
      notifyError(`Error updating contact. Check the lead owner and contact settings.`)
    } finally {
      setContactInFlight(false)
    }
  }

  const reloadContact = async () => {
    trace(`contacts`, `reloadContact V2`)
    setContactInFlight(true)
    try {
      const fetchResult = await contactStore.fetchOne(contact.id!, { requestPolicy: `network-only` })
      if (fetchResult.error) {
        logError(`Error reloading contact V2`, fetchResult.error.message)
        notifyError(`Error reloading contact`)
        return
      }
      if (contactStore.current) setIsDirtyForm(false)
    } finally {
      setContactInFlight(false)
    }
  }

  const handleCloseModal = async (data: Record<string, unknown>) => {
    closeModal()
    await updateContact(data)
  }

  const handleSubmitFollowUp = async (data: Record<string, unknown>, followUpResult?: FollowUpJson) => {
    await updateContact(data)
    if (followUpResult && onActivityAdded) {
      onActivityAdded({
        id: followUpResult.id,
        type: `FollowUp`,
        typeId: followUpResult.id,
        activityDate: followUpResult.followUpDate ?? new Date().toISOString(),
        description: followUpResult.description,
        owner: followUpResult.owner,
      } as ActivityJson)
    }
  }

  const handleCancel = () => {
    setIsDirtyForm(false)
    setFormKey((prev) => prev + 1)
  }

  const handleSubmit = async () => {
    let form = document.getElementById(`contact-drawer-form`) as HTMLFormElement
    let data: Record<string, unknown> = Object.fromEntries(new FormData(form).entries())

    // Merge controlled object values (e.g. leadOwner) that aren't in native FormData
    const formValues = formCtx.getValues()
    const leadOwnerStr = formValues.get(`leadOwner`) as string | undefined
    if (leadOwnerStr) {
      try {
        data.leadOwner = JSON.parse(leadOwnerStr)
      } catch (e) {
        logError(`Failed to parse leadOwner from form values`, e)
      }
    }

    let result = await contactValidatorSchema.validate(data)
    debug(`contacts`, `update V2`, data, result)
    if (result.error) {
      logError(`error saving contact V2`, result.error)
      notifyError(`Error Saving`)
      return
    }
    const contactData = result.data
    if (!contactData) return
    if (
      contactData.leadStatus !== contact.leadStatus &&
      [`In Progress`, `Scheduled`].includes(contactData.leadStatus ?? ``)
    ) {
      showModal(
        <div className="w-[650px]">
          <div className="w-full py-4 text-sm font-medium leading-4 text-default">Do you want to log a follow up?</div>
          <div className="flex justify-end border-t border-default py-6">
            <Button
              label="No"
              mode="white"
              className="btn btn-secondary btn-md"
              onClick={() => handleCloseModal(contactData)}
            />
            <Button
              label="Yes"
              mode="primary"
              className="btn btn-primary btn-md ml-4"
              onClick={() =>
                showModal(
                  <FollowUpForm
                    contact={contact}
                    onSubmit={(_activity, followUpResult) => handleSubmitFollowUp(contactData, followUpResult)}
                    onComplete={() => {
                      closeModal() // close FollowUpForm (nested)
                      closeModal() // close "Do you want to log a follow up?" (parent)
                    }}
                  />,
                  {
                    title: `Add Follow up`,
                    isFullScreen: false,
                    overflowHidden: false,
                    removePadding: true,
                    useRadix: true,
                  },
                  false,
                )
              }
            />
          </div>
        </div>,
        {
          title: `Follow up?`,
          isFullScreen: false,
          showActions: false,
          useRadix: true,
        },
      )
    } else if (contactData.leadStatus === `Closed Lost` && contactData.leadStatus !== contact.leadStatus) {
      showModal(
        <ClosedLostForm
          contact={contact}
          handleSubmit={async (data) => {
            if (!data) return
            trace(`qualified`, `submit closed lost V2`, data)
            const closedLostReason = data.closedLostReason
            if (!closedLostReason) return
            const isQualified = isSMCOrg(user.orgId)
              ? ![`Service Not Offered`, `Insurance: State / HMO`, `Office Too Far`].includes(closedLostReason)
              : data.disqualified === `false`
            const result = await gqlMutation(CLOSE_LOST_MUTATION, {
              id: contact.id!,
              input: {
                leadStatus: `Closed Lost`,
                closedLostReason,
                isQualified,
                note: data.note,
                ownerId: data.owner?.id,
                ownerName: data.owner?.name,
              },
            })
            if (result.error) {
              logError(`error closing lead as lost V2`, result.error.message)
              notifyError(`Error Saving Contact`)
              return
            }
            notifySuccess(`Save Successful`)
            closeModal()
            await reloadContact()
          }}
          handleCancel={closeModal}
        />,
        {
          title: `Closed lost`,
          isFullScreen: false,
          overflowHidden: false,
          useRadix: true,
        },
      )
    } else {
      trace(`qualified`, `submit closed lost else V2`, result.data)
      await updateContact(result.data)
    }
  }

  return (
    <div className="flex h-full flex-col">
      {isMarkedNonPatient && <RevertNonPatient contact={contact} onChange={onChange} />}
      <SmartForm<ContactFormValues>
        key={formKey}
        id="contact-drawer-form"
        formClassNames="grow overflow-y-auto"
        className="!mt-0 !flex flex-col gap-y-1"
        subaction={SubAction.EDIT}
        validator={contactValidatorSchema}
        defaultValues={defaultValues}
        hiddenInputs={[{ name: `id`, value: contact.id }]}
        onTouchedFields={handleTouchedFields}
        preventSubmit
        hideButtons
      >
        <PmsIntegration contact={contact} location={location} />
        <ContactTags contact={contact} isAgentOnly={isAgentOnly} />
        <ContactDrawerContainer
          contact={contact}
          isIntegrated={isIntegrated}
          onContactChange={onChange}
          isAgentOnly={isAgentOnly}
          isAIThread={isAIThread}
        />
      </SmartForm>
      {isDirtyForm && !isAgentOnly && (
        <div className="sticky bottom-1 w-full rounded-md bg-surface-muted px-4 py-3">
          <div className="flex justify-center gap-x-2 align-middle">
            <Button
              type="button"
              label="Cancel"
              disabled={contactInFlight}
              mode="white"
              className="w-full"
              onClick={handleCancel}
            />
            <Button
              type="button"
              label="Save"
              disabled={contactInFlight}
              mode="primary"
              className="w-full"
              onClick={handleSubmit}
            />
          </div>
        </div>
      )}
    </div>
  )
})
